// src/components/cards/QuoteDetailCard.tsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, useTheme } from '@mui/material';
import {
  Event as DateIcon,
  CalendarMonth as LengthIcon,
  Description as NotesIcon,
  Pets as PetsIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  SwapHoriz as ConvertedIcon,
  CalendarToday as ExpiryIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { ResponsiveFormGrid } from '../forms/shared';
import { formatQuoteExpiry } from '../lists/BookingsList/bookingsListUtils';
import {
  BeingDetailsField,
  MetadataField,
} from '../forms/shared/DisplayFieldVariants';
import {
  FieldLabelContainerMultiline,
  FieldLabel,
  FieldInputContainerMultiline,
  FormFieldRowMultiline,
  SectionHeader,
  SectionHeaderWrapper,
} from '../forms/shared';
import { FloatingAccessButton } from '../common/FloatingAccessButton';
import BookingCardPetsList from '../forms/shared/BookingCardPetsList';
import BookingCardSurchargeList from '../forms/shared/BookingCardSurchargeList';
import BookingCardTotals from '../forms/shared/BookingCardTotals';
import BookingCardInvoiceHistory from '../forms/shared/BookingCardInvoiceHistory';
import BottomMenu, { BottomMenuItem } from '../menus/BottomMenu';
import { useErrorHandler } from '../../errors';
import {
  bookingsAPI,
  BookingFormEmailLogEntry,
  quotesAPI,
  QuoteEmailLogEntry,
  ConvertQuoteRequest,
} from '../../services/api';
import { useGlobalToast } from '../../App';
import { useHeaderDrawer } from '../../contexts/HeaderDrawerContext';
import { useAnchorRect } from '../../hooks/useAnchorRect';
import EmailQuoteDialog from './shared/dialogs/EmailQuoteDialog';
import EmailBookingFormDialog from './shared/dialogs/EmailBookingFormDialog';
import ConvertQuoteDialog, {
  ConvertQuoteSketchPet,
} from './shared/dialogs/ConvertQuoteDialog';
import { getCustomIcon } from '../../utils/customIcons';

interface QuotePetResponse {
  id: number;
  petId: number;
  petName: string;
  petType: string;
  petBreed: string;
  petSize: string;
  isSketch?: boolean;
  useSharingRate: boolean;
  hasHeater: boolean;
  dailyRate: number;
  numberOfNights: number;
  accommodationTotal: number;
  groomingTotal: number;
  petTotal: number;
  lineItemDiscount?: number;
  lineItemDiscountPercentage?: number;
  rateType: string | null;
}

interface QuoteKennelResponse {
  id: number;
  kennelNumber: number;
  kennelSide: string;
  kennelSideId: number;
  size?: string;
  heaterNights: number;
  heaterDailyRate: number;
}

interface QuoteSurchargeResponse {
  id: number;
  reason: string;
  amount: number;
}

interface QuoteDetailCardProps {
  quote: {
    id: number;
    clientId: number;
    clientName: string;
    clientEmail?: string;
    checkInDate: string;
    checkOutDate: string;
    numberOfNights: number;
    status: string;
    notes: string;
    subTotal: number;
    discountAmount: number;
    taxAmount: number;
    totalAmount: number;
    depositAmount?: number;
    createdAt: string;
    updatedAt?: string;
    createdByUsername?: string;
    updatedByUsername?: string;
    // Set when this quote has been converted to a real booking. The card
    // shows a "Converted to Booking #N on <date>" line and disables the
    // convert/edit actions in the bottom menu.
    convertedToBookingId?: number | null;
    convertedAt?: string | null;
    quoteExpiresAt?: string | null;
    pets: QuotePetResponse[];
    kennels?: QuoteKennelResponse[];
    surcharges?: QuoteSurchargeResponse[];
  };
  clientEmail?: string;
  clientPhone?: string;
  clientIsProspect?: boolean;
  onEdit?: () => void;
  onRefresh?: () => void;
}

const StyledCard = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#ffffff',
}));

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const getPetSummary = (pets: QuotePetResponse[]): string => {
  const petTypeMap = new Map<string, number>();
  pets.forEach((pet) => {
    const count = petTypeMap.get(pet.petType) || 0;
    petTypeMap.set(pet.petType, count + 1);
  });
  return Array.from(petTypeMap.entries())
    .map(([type, count]) => `${count} ${type}${count !== 1 ? 's' : ''}`)
    .join(', ');
};

const QuoteDetailCard: React.FC<QuoteDetailCardProps> = ({
  quote,
  clientEmail,
  clientPhone,
  clientIsProspect = false,
  onEdit,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { handleError } = useErrorHandler();
  const { showSuccess, showError } = useGlobalToast();
  const { isHeaderDrawerOpen } = useHeaderDrawer();

  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertSubmitting, setConvertSubmitting] = useState(false);

  const [quoteEmailDialogOpen, setQuoteEmailDialogOpen] = useState(false);
  const [quoteEmailSending, setQuoteEmailSending] = useState(false);
  const [quoteEmailLog, setQuoteEmailLog] = useState<QuoteEmailLogEntry[]>([]);

  const [bookingFormDialogOpen, setBookingFormDialogOpen] = useState(false);
  const [bookingFormSending, setBookingFormSending] = useState(false);
  const [bookingFormEmailLog, setBookingFormEmailLog] = useState<
    BookingFormEmailLogEntry[]
  >([]);

  const [bottomMenuOpen, setBottomMenuOpen] = useState(false);
  const bottomMenuFabRef = useRef<HTMLDivElement>(null);
  const bottomMenuAnchor = useAnchorRect(bottomMenuFabRef, bottomMenuOpen);

  const resolvedClientEmail = quote.clientEmail || clientEmail;

  const sketchPets: ConvertQuoteSketchPet[] = quote.pets
    .filter((p) => p.isSketch)
    .map((p) => ({
      petId: p.petId,
      petName: p.petName,
      petType: p.petType,
      petBreed: p.petBreed,
    }));

  const loadQuoteEmailLog = useCallback(async () => {
    try {
      const log = await quotesAPI.getEmailLog(quote.id);
      setQuoteEmailLog(log);
    } catch {
      // non-critical
    }
  }, [quote.id]);

  const loadBookingFormEmailLog = useCallback(async () => {
    try {
      const log = await bookingsAPI.getBookingFormLog(quote.id);
      setBookingFormEmailLog(log);
    } catch {
      // non-critical
    }
  }, [quote.id]);

  useEffect(() => {
    loadQuoteEmailLog();
    loadBookingFormEmailLog();
  }, [loadQuoteEmailLog, loadBookingFormEmailLog]);

  const isConverted = quote.convertedToBookingId != null;

  // Existing clients send just pets[]; prospects also send clientPatch and kennels.
  const handleConvertConfirm = async (payload: ConvertQuoteRequest) => {
    try {
      setConvertSubmitting(true);
      const response = await quotesAPI.convert(quote.id, payload);
      showSuccess();
      setConvertDialogOpen(false);
      navigate(`/booking/${response.newBookingId}`);
    } catch (error: any) {
      // A refusal (pet already booked, kennel taken) names the reason.
      showError(error.response?.data?.message || 'Could not convert quote.');
    } finally {
      setConvertSubmitting(false);
    }
  };

  const handleDownloadQuote = async () => {
    try {
      const pdfBlob = await quotesAPI.downloadPdf(quote.id);
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      const sanitizedName = quote.clientName
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase();
      link.href = url;
      link.download = `Quote_QUO-${String(quote.id).padStart(4, '0')}_${sanitizedName}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      handleError(error, { context: 'downloading quote PDF', showToast: true });
    }
  };

  const handleEmailQuote = async (includeBookingForm: boolean) => {
    setQuoteEmailSending(true);
    try {
      await quotesAPI.emailQuote(quote.id, includeBookingForm);
      showSuccess();
      loadQuoteEmailLog();
      if (includeBookingForm) loadBookingFormEmailLog();
    } catch (error) {
      handleError(error, { context: 'emailing quote', showToast: false });
      showError();
    } finally {
      setQuoteEmailSending(false);
      setQuoteEmailDialogOpen(false);
    }
  };

  const handleSendBookingForm = async () => {
    setBookingFormSending(true);
    try {
      await bookingsAPI.sendBookingForm(quote.id);
      showSuccess();
      loadBookingFormEmailLog();
    } catch (error) {
      handleError(error, { context: 'sending booking form', showToast: false });
      showError();
    } finally {
      setBookingFormSending(false);
      setBookingFormDialogOpen(false);
    }
  };

  const getMetadataValue = () => {
    const createdDate = quote.createdAt
      ? new Date(quote.createdAt).toLocaleString()
      : 'Unknown';

    const hasUpdate =
      quote.updatedAt &&
      quote.createdAt &&
      new Date(quote.updatedAt).toISOString() !==
        new Date(quote.createdAt).toISOString();

    const createdLine = quote.createdByUsername
      ? `Created: ${createdDate} by ${quote.createdByUsername}`
      : `Created: ${createdDate}`;

    if (hasUpdate && quote.updatedAt) {
      const updatedDate = new Date(quote.updatedAt).toLocaleString();
      const updatedLine = quote.updatedByUsername
        ? `Updated: ${updatedDate} by ${quote.updatedByUsername}`
        : `Updated: ${updatedDate}`;
      return (
        <Box
          sx={{
            whiteSpace: 'nowrap',
            overflowX: 'auto',
            overflowY: 'visible',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {createdLine}
          <br />
          {updatedLine}
        </Box>
      );
    }

    return (
      <Box component="span" sx={{ display: 'block', padding: '0.75em 0' }}>
        <Box
          sx={{
            whiteSpace: 'nowrap',
            overflowX: 'auto',
            overflowY: 'visible',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {createdLine}
        </Box>
      </Box>
    );
  };

  const quoteMenuItems: BottomMenuItem[] = [
    {
      key: 'download-quote',
      label: 'Download Quote',
      onClick: handleDownloadQuote,
    },
    {
      key: 'email-quote',
      label: 'Email Quote',
      onClick: () => setQuoteEmailDialogOpen(true),
      disabled: !resolvedClientEmail,
    },
    {
      key: 'email-booking-form',
      label: 'Email Booking Form',
      onClick: () => setBookingFormDialogOpen(true),
      disabled: !resolvedClientEmail,
    },
    // Convert and Edit are hidden once the quote has been converted - the
    // retained quote is a historical record and must not change after that.
    ...(isConverted
      ? []
      : [
          {
            key: 'convert-to-booking',
            label: 'Convert to Booking',
            onClick: () => setConvertDialogOpen(true),
          },
        ]),
    ...(onEdit && !isConverted
      ? [
          {
            key: 'edit-quote',
            label: 'Edit Quote',
            onClick: onEdit,
          },
        ]
      : []),
  ];

  return (
    <StyledCard>
      <Box>
        <ResponsiveFormGrid
          alternateRowColors
          breakpointPadding={{ default: '0', medium: '0', large: '0' }}
          container
          spacing={0}
        >
          {clientIsProspect && (
            <>
              <BeingDetailsField
                label="Email"
                valuePadding="2.4px 0 2.4px 1rem"
                value={clientEmail || ''}
                startIcon={<EmailIcon />}
                copyable
                emptyText="Not provided"
                hideColon
                labelFontWeight={700}
                valuePaddingMobile="2.4px 0"
              />
              <BeingDetailsField
                label="Telephone"
                valuePadding="2.4px 0 2.4px 1rem"
                value={clientPhone || ''}
                startIcon={<PhoneIcon />}
                copyable
                emptyText="Not provided"
                hideColon
                labelFontWeight={700}
                valuePaddingMobile="2.4px 0"
              />
            </>
          )}
          <BeingDetailsField
            label="Check-in"
            valuePadding="2.4px 0 2.4px 1rem"
            value={formatDate(quote.checkInDate)}
            startIcon={<DateIcon />}
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />
          <BeingDetailsField
            label="Check-out"
            valuePadding="2.4px 0 2.4px 1rem"
            value={formatDate(quote.checkOutDate)}
            startIcon={<DateIcon />}
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />
          <BeingDetailsField
            label="Length"
            valuePadding="2.4px 0 2.4px 1rem"
            value={`${quote.numberOfNights} night${quote.numberOfNights !== 1 ? 's' : ''}`}
            startIcon={<LengthIcon />}
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />
          {quote.quoteExpiresAt && quote.convertedToBookingId == null && (
            <BeingDetailsField
              label="Expiry Date"
              valuePadding="2.4px 0 2.4px 1rem"
              value={formatQuoteExpiry(quote.quoteExpiresAt).label}
              startIcon={<ExpiryIcon />}
              hideColon
              labelFontWeight={700}
              valuePaddingMobile="2.4px 0"
            />
          )}
          <BeingDetailsField
            label="Pets"
            valuePadding="2.4px 0 2.4px 1rem"
            value={getPetSummary(quote.pets)}
            startIcon={<PetsIcon />}
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />

          {isConverted && quote.convertedToBookingId != null && (
            <BeingDetailsField
              label="Converted"
              valuePadding="2.4px 0 2.4px 1rem"
              value={
                <Box
                  component="span"
                  role="link"
                  tabIndex={0}
                  onClick={() =>
                    navigate(`/booking/${quote.convertedToBookingId}`)
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/booking/${quote.convertedToBookingId}`);
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 600,
                    '&:hover': { textDecoration: 'underline' },
                    whiteSpace: 'nowrap',
                    overflowX: 'auto',
                    overflowY: 'visible',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    display: 'block',
                    width: '98%',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}
                >
                  Booking #{quote.convertedToBookingId}
                  {quote.convertedAt
                    ? ` on ${formatDate(quote.convertedAt)}`
                    : ''}
                </Box>
              }
              startIcon={getCustomIcon('convert', '2.4rem')}
              hideColon
              labelFontWeight={700}
              valuePaddingMobile="2.4px 0"
            />
          )}

          {clientIsProspect && !isConverted && (
            <BeingDetailsField
              label="Converted"
              valuePadding="2.4px 0 2.4px 1rem"
              value="Unconverted prospect"
              startIcon={getCustomIcon('convert', '2.4rem')}
              hideColon
              labelFontWeight={700}
              valuePaddingMobile="2.4px 0"
            />
          )}
          {quote.notes && (
            <BeingDetailsField
              label="Notes"
              valuePadding="2.4px 0 2.4px 1rem"
              value={quote.notes}
              startIcon={<NotesIcon />}
              hideColon
              labelFontWeight={700}
              valuePaddingMobile="2.4px 0"
            />
          )}
        </ResponsiveFormGrid>
      </Box>

      {/* Pets */}
      {quote.pets.length > 0 && (
        <>
          <SectionHeaderWrapper>
            <FieldLabelContainerMultiline
              sx={{
                paddingTop: '0.2rem',
                '@media (max-width: 600px)': { display: 'none' },
              }}
            />
            <SectionHeader
              sx={{
                '& > p': { fontSize: '2rem' },
                '@media (max-width: 600px)': { pl: 0 },
              }}
            >
              <FieldLabel>Pets</FieldLabel>
            </SectionHeader>
          </SectionHeaderWrapper>
          <FormFieldRowMultiline>
            <FieldLabelContainerMultiline
              sx={{
                paddingTop: '0.2rem',
                '@media (max-width: 600px)': { display: 'none' },
              }}
            />
            <FieldInputContainerMultiline
              sx={{
                pt: 0,
                pr: 0,
                pb: 0,
                pl: 0,
                '@media (max-width: 600px)': { padding: 0 },
              }}
            >
              <BookingCardPetsList pets={quote.pets} />
            </FieldInputContainerMultiline>
          </FormFieldRowMultiline>
        </>
      )}

      {/* Surcharges */}
      {(quote.surcharges?.length ?? 0) > 0 && (
        <>
          <SectionHeaderWrapper
            sx={{ '@media (max-width: 600px)': { pt: '0.5rem' } }}
          >
            <FieldLabelContainerMultiline
              sx={{
                paddingTop: '0.2rem',
                '@media (max-width: 600px)': { display: 'none' },
              }}
            />
            <SectionHeader
              sx={{
                '& > p': { fontSize: '2rem' },
                '@media (max-width: 600px)': { pl: 0 },
              }}
            >
              <FieldLabel>Surcharges</FieldLabel>
            </SectionHeader>
          </SectionHeaderWrapper>
          <FormFieldRowMultiline>
            <FieldLabelContainerMultiline
              sx={{
                paddingTop: '0.2rem',
                '@media (max-width: 600px)': { display: 'none' },
              }}
            />
            <FieldInputContainerMultiline
              sx={{ pb: 0, '@media (max-width: 600px)': { padding: 0 } }}
            >
              <BookingCardSurchargeList surcharges={quote.surcharges!} />
            </FieldInputContainerMultiline>
          </FormFieldRowMultiline>
        </>
      )}

      {/* Totals */}
      <SectionHeaderWrapper
        sx={{ '@media (max-width: 600px)': { pt: '0.5rem' } }}
      >
        <FieldLabelContainerMultiline
          sx={{
            paddingTop: '0.2rem',
            '@media (max-width: 600px)': { display: 'none' },
          }}
        />
        <SectionHeader
          sx={{
            '& > p': { fontSize: '2rem' },
            '@media (max-width: 600px)': { pl: 0 },
          }}
        >
          <FieldLabel>Totals</FieldLabel>
        </SectionHeader>
      </SectionHeaderWrapper>
      <FormFieldRowMultiline>
        <FieldLabelContainerMultiline
          sx={{
            paddingTop: '0.2rem',
            '@media (max-width: 600px)': { display: 'none' },
          }}
        />
        <FieldInputContainerMultiline
          sx={{ pb: 0, '@media (max-width: 600px)': { padding: 0 } }}
        >
          <BookingCardTotals
            pets={quote.pets}
            kennels={quote.kennels}
            surcharges={quote.surcharges}
            depositAmount={quote.depositAmount}
            isQuotation={true}
          />
        </FieldInputContainerMultiline>
      </FormFieldRowMultiline>

      {/* Quote Email History */}
      <SectionHeaderWrapper
        sx={{ '@media (max-width: 600px)': { pt: '0.5rem' } }}
      >
        <FieldLabelContainerMultiline
          sx={{
            paddingTop: '0.2rem',
            '@media (max-width: 600px)': { display: 'none' },
          }}
        />
        <SectionHeader
          sx={{
            '& > p': { fontSize: '2rem' },
            '@media (max-width: 600px)': { pl: 0 },
          }}
        >
          <FieldLabel>Quote Email History</FieldLabel>
        </SectionHeader>
      </SectionHeaderWrapper>
      <FormFieldRowMultiline>
        <FieldLabelContainerMultiline
          sx={{
            paddingTop: '0.2rem',
            '@media (max-width: 600px)': { display: 'none' },
          }}
        />
        <FieldInputContainerMultiline
          sx={{ pb: 0, '@media (max-width: 600px)': { padding: 0 } }}
        >
          <BookingCardInvoiceHistory
            emailLog={quoteEmailLog}
            label="Quote"
            onSendNow={() => setQuoteEmailDialogOpen(true)}
            sendDisabled={!resolvedClientEmail}
          />
        </FieldInputContainerMultiline>
      </FormFieldRowMultiline>

      {/* Booking Form Email History */}
      <SectionHeaderWrapper
        sx={{ '@media (max-width: 600px)': { pt: '0.5rem' } }}
      >
        <FieldLabelContainerMultiline
          sx={{
            paddingTop: '0.2rem',
            '@media (max-width: 600px)': { display: 'none' },
          }}
        />
        <SectionHeader
          sx={{
            '& > p': { fontSize: '2rem' },
            '@media (max-width: 600px)': { pl: 0 },
          }}
        >
          <FieldLabel>Booking Form History</FieldLabel>
        </SectionHeader>
      </SectionHeaderWrapper>
      <FormFieldRowMultiline>
        <FieldLabelContainerMultiline
          sx={{
            paddingTop: '0.2rem',
            '@media (max-width: 600px)': { display: 'none' },
          }}
        />
        <FieldInputContainerMultiline
          sx={{ pb: 10, '@media (max-width: 600px)': { padding: 0 } }}
        >
          <BookingCardInvoiceHistory
            emailLog={bookingFormEmailLog}
            label="Booking Form"
            onSendNow={() => setBookingFormDialogOpen(true)}
            sendDisabled={!resolvedClientEmail}
          />
        </FieldInputContainerMultiline>
      </FormFieldRowMultiline>

      {/* Metadata */}
      <ResponsiveFormGrid
        breakpointPadding={{
          default: '0.5rem',
          medium: '0.03rem',
          large: '1rem',
        }}
        container
        spacing={0}
        sx={{ marginTop: 0, '@media (max-width: 600px)': { marginTop: 5 } }}
      >
        <MetadataField
          label={`Quote #${quote.id}`}
          labelFontSize="2rem"
          labelFontSizeMobile="1.8rem"
          labelFontWeight={700}
          valueFontSizeMobile="1.4rem"
          value={getMetadataValue()}
          hideColon
          valuePaddingMobile="0.5rem 1.5rem 5rem 1.5rem"
        />
      </ResponsiveFormGrid>

      {!isHeaderDrawerOpen && (
        <FloatingAccessButton
          ref={bottomMenuFabRef}
          icon={bottomMenuOpen ? <CloseIcon /> : <MenuIcon />}
          hoverIcon={bottomMenuOpen ? <CloseIcon /> : <MenuIcon />}
          onClick={() => setBottomMenuOpen((v) => !v)}
          iconScale={2}
          aria-label={
            bottomMenuOpen ? 'Close actions menu' : 'Open actions menu'
          }
          backgroundColor={
            bottomMenuOpen
              ? theme.palette.secondary.main
              : theme.palette.primary.main
          }
          hoverBackgroundColor={
            bottomMenuOpen
              ? theme.palette.primary.dark
              : theme.palette.secondary.dark
          }
          bottom="2rem"
          right="10%"
          sx={{
            zIndex: 1300,
            border: '2px solid #fff',
            '@media (max-width: 600px)': {
              bottom: '1rem',
              right: '1rem',
              '& svg': { transform: 'scale(1.2)' },
            },
            '@media (min-width: 601px)': {
              width: '75px',
              height: '75px',
              minWidth: '75px',
              minHeight: '75px',
            },
            '& svg': {
              transition: 'transform 0.3s ease-in-out',
              transform: 'scale(1.4)',
            },
            '&:active': {
              backgroundColor: bottomMenuOpen
                ? 'secondary.light'
                : 'primary.dark',
            },
            '&:hover svg': {
              opacity: 0.8,
              transform: bottomMenuOpen
                ? 'scale(2.1) rotate(-180deg)'
                : 'scale(2.1) rotate(180deg)',
            },
          }}
        />
      )}

      <BottomMenu
        open={bottomMenuOpen}
        onClose={() => setBottomMenuOpen(false)}
        items={quoteMenuItems}
        right={bottomMenuAnchor ? `${bottomMenuAnchor.right}px` : '1rem'}
        bottom={bottomMenuAnchor ? `${bottomMenuAnchor.bottom}px` : '40px'}
        sx={{
          '@media (max-width: 900px)': {
            marginRight: '.1rem',
            marginBottom: '.1rem',
          },
          '@media (min-width: 900px)': {
            marginRight: '1rem',
            marginBottom: '1rem',
          },
        }}
      />

      {/* Dialogs */}
      <ConvertQuoteDialog
        open={convertDialogOpen}
        onClose={() => setConvertDialogOpen(false)}
        onConfirm={handleConvertConfirm}
        sketchPets={sketchPets}
        clientName={quote.clientName}
        clientIsProspect={clientIsProspect}
        submitting={convertSubmitting}
        // Prospect-only context (ignored when clientIsProspect=false but
        // always passed since the data is already on the quote object).
        clientId={quote.clientId}
        checkInDate={quote.checkInDate}
        checkOutDate={quote.checkOutDate}
        numberOfNights={quote.numberOfNights}
      />

      <EmailQuoteDialog
        open={quoteEmailDialogOpen}
        onClose={() => setQuoteEmailDialogOpen(false)}
        onConfirm={handleEmailQuote}
        clientName={quote.clientName}
        clientEmail={resolvedClientEmail ?? ''}
        sending={quoteEmailSending}
      />

      <EmailBookingFormDialog
        open={bookingFormDialogOpen}
        onClose={() => setBookingFormDialogOpen(false)}
        onConfirm={handleSendBookingForm}
        clientName={quote.clientName}
        clientEmail={resolvedClientEmail ?? ''}
        sending={bookingFormSending}
      />
    </StyledCard>
  );
};

export default QuoteDetailCard;
